import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Leaf, Download, Package, FileText, Loader2, ChevronRight, Truck, CheckCircle, Clock, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n));

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: Clock, color: "bg-amber-50 text-amber-700 border-amber-200" },
  paid: { label: "Paid", icon: CheckCircle, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  complete: { label: "Complete", icon: CheckCircle, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", icon: XCircle, color: "bg-red-50 text-red-600 border-red-200" },
};

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/orders`);
        setOrders(res.data.orders);
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
        else toast.error("Failed to load orders");
      }
      setLoading(false);
    };
    fetchOrders();
  }, [navigate]);

  const handleDownload = async (orderId) => {
    setDownloadingId(orderId);
    try {
      const res = await axios.get(`${API}/orders/${orderId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("PDF opened!");
    } catch (err) {
      toast.error(err.response?.status === 402 ? "Payment required" : "Download failed");
    }
    setDownloadingId(null);
  };

  if (loading) return (<div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center"><div className="w-10 h-10 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" /></div>);

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="order-history-page">
      <nav className="sticky top-0 z-40 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center"><Leaf className="w-4 h-4 text-white" /></div>
              <span className="font-serif text-lg font-medium text-[#2D3A2D]">My Story</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-[#D4C9B8]" />
            <span className="font-sans text-sm text-[#4A5D4A]">My Orders</span>
          </div>
          <Link to="/app" className="font-sans text-sm text-[#7A8A7A] hover:text-[#4A5D4A] transition-colors">Back to App</Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl font-medium text-[#2D3A2D] mb-2" data-testid="orders-title">My Orders</h1>
        <p className="font-sans text-sm text-[#7A8A7A] mb-8">View and download your purchased books</p>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white/60 border border-[#D4C9B8]/40 rounded-2xl" data-testid="no-orders">
            <Package className="w-12 h-12 text-[#D4C9B8] mx-auto mb-4" />
            <p className="font-sans text-base text-[#7A8A7A]">No orders yet</p>
            <p className="font-sans text-sm text-[#9A9A8A] mt-1">Visit the Virtual Studio to export your book</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((order) => {
              const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = sc.icon;
              const isPaid = ["paid", "complete"].includes(order.status);
              return (
                <div key={order.id} className="bg-white/80 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid={`order-${order.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-serif text-lg font-medium text-[#2D3A2D]">{order.book_title}</h3>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-sans font-medium border ${sc.color}`}>
                          <StatusIcon className="w-3 h-3" />{sc.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 font-sans text-xs text-[#7A8A7A]">
                        <span>{order.tier} pages</span>
                        <span>{order.track === "physical" ? (order.product_type === "physical_hardcover" ? "Hardcover + PDF" : "Softcover + PDF") : "Digital PDF"}</span>
                        {order.color_print && <span>Color pages</span>}
                        <span>₹{formatINR(order.price)}</span>
                        <span>{new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </div>
                      {order.track === "physical" && order.shipping_address && (
                        <div className="mt-3 flex items-start gap-2">
                          <Truck className="w-3.5 h-3.5 text-[#7A8A7A] mt-0.5 flex-shrink-0" />
                          <p className="font-sans text-xs text-[#7A8A7A]">
                            {order.shipping_address.full_name}, {order.shipping_address.city}, {order.shipping_address.state} — {order.shipping_address.pincode}
                            {order.fulfillment_status === "sent" && <span className="ml-2 text-emerald-600 font-medium">Sent to printer</span>}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isPaid && (
                        <button
                          onClick={() => handleDownload(order.id)}
                          disabled={downloadingId === order.id}
                          className="flex items-center gap-2 px-4 py-2.5 bg-[#4A5D4A] text-white rounded-xl font-sans text-sm font-medium hover:bg-[#2D3A2D] transition-colors disabled:opacity-60"
                          data-testid={`download-${order.id}`}
                        >
                          {downloadingId === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          Download PDF
                        </button>
                      )}
                      <Link to={`/studio/${order.book_id}`} className="px-4 py-2.5 border border-[#D4C9B8] rounded-xl font-sans text-sm text-[#7A8A7A] hover:bg-[#F8F6F1] transition-colors">
                        Studio
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
